import { Skeleton as MantineSkeleton } from '@mantine/core'
import type React from 'react'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type SkeletonVariant = 'text' | 'circle' | 'rect'

export type SkeletonProps = {
  /** スケルトンの形状
   * - `text`: テキスト行（高さ固定・角丸）
   * - `circle`: 円形（アバター等）
   * - `rect`: 矩形（画像・カード等）
   */
  variant?: SkeletonVariant
  /** 幅（px または CSS 値）。circle 時は直径として使用。デフォルト: '100%' */
  width?: number | string
  /** 高さ（px）。text=16 / circle=width / rect=80 がデフォルト */
  height?: number
  /** アニメーションを有効にするか（デフォルト: true） */
  animate?: boolean
  className?: string
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
/**
 * コンテンツ読み込み中に表示するプレースホルダー。
 *
 * @example
 * // テキスト行
 * <Skeleton variant="text" width="80%" />
 *
 * // アバター
 * <Skeleton variant="circle" width={40} />
 *
 * // カード画像
 * <Skeleton variant="rect" height={160} />
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width = '100%',
  height,
  animate = true,
  className,
}) => {
  if (variant === 'circle') {
    const size = typeof width === 'number' ? width : 40
    return <MantineSkeleton circle height={size} animate={animate} className={className} />
  }

  const resolvedHeight = height ?? (variant === 'text' ? 16 : 80)
  const radius = variant === 'text' ? 'sm' : 'md'

  return (
    <MantineSkeleton height={resolvedHeight} width={width} radius={radius} animate={animate} className={className} />
  )
}

// ─────────────────────────────────────────────
// 複数行テキストのプリセット
// ─────────────────────────────────────────────
export type SkeletonTextProps = {
  /** 行数（デフォルト: 3） */
  lines?: number
  /** 最終行の幅（デフォルト: '60%'）*/
  lastLineWidth?: number | string
  animate?: boolean
  className?: string
}

/**
 * 複数行のテキストスケルトン。段落の読み込み表示に使用。
 *
 * @example
 * <SkeletonText lines={4} lastLineWidth="40%" />
 */
export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  lastLineWidth = '60%',
  animate = true,
  className,
}) => {
  return (
    <div className={['flex flex-col gap-2', className ?? ''].join(' ').trim()}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          // biome-ignore lint/suspicious/noArrayIndexKey: 静的なスケルトン行のため index を key に使用
          key={i}
          variant="text"
          width={i === lines - 1 ? lastLineWidth : '100%'}
          animate={animate}
        />
      ))}
    </div>
  )
}
